<template>
  <div class="card has-padding-20">
    <!-- Heading -->
    <div class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <button class="button is-danger" @click="deleteDangerousGood">Delete this Dangerous Good</button>
      </div>
    </div>

    <validation-observer :ref="observerRef" tag="div" v-slot="{invalid, valid, untouched, errors}">
      <div
        class="has-margin-bottom-40 has-margin-top-10 has-border-bottom"
        v-if="hasDangerousGoodInfo"
      >
        <h2
          class="is-size-4 has-text-weight-bold is-uppercase has-letter-spacing-3 is-black"
        >Dangerous Good Properties</h2>
      </div>
      <div class="columns is-multiline">
      <!-- UN Number -->
      <div class="column is-4 ">
        <label class="label">UN Number</label>
        <multiselect
          v-model="selectedDangerousGoodInfo"
          deselect-label="UN Number must be selected"
          track-by="id"
          label="un_number"
          placeholder="Select a UN Number"
          :options="dangerousGoodsInfo"
          :searchable="true"
          :allow-empty="false"
          @select="(option,id) => updateDangerousGoodInfoId(option)"
        >
          <template slot="singleLabel" slot-scope="{ option }">{{ option.un_number }}</template>
        </multiselect>
      </div>
      </div>

      <template v-if="hasDangerousGoodInfo">
        <div class="columns is-multiline">
          <!-- Proper Shipping Name -->
          <div class="column is-4">
            <b-field label="Proper Shipping Name">
              <b-input
                v-model="selectedDangerousGoodInfo.proper_shipping_name"
                name="properShippingName"
                placeholder="Proper Shipping Name"
                v-validate="'required'"
                disabled
              ></b-input>
            </b-field>
          </div>

          <div class="column is-4">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.chemical_name"
              :vuexKey="'chemical_name'"
              :label="'Chemical Name'"
              :errorLabel="`Chemical Name`"
              :name="'chemicalName'"
              :placeholder="'Chemical Name'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Sub Risk -->
          <div class="column is-4">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.sub_risk"
              :vuexKey="'sub_risk'"
              :label="'Sub Risk'"
              :errorLabel="`Sub Risk`"
              :name="'subRisk'"
              :placeholder="'Sub Risk'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- IMO class -->
          <div class="column is-4">
            <b-field label="IMO Class">
              <b-input
                v-model="selectedDangerousGoodInfo.imo_class"
                name="imoClass"
                placeholder="IMO Class"
                v-validate="'required'"
                disabled
              ></b-input>
            </b-field>
          </div>

          <!-- Packaging Group -->
          <div class="column is-4">
            <b-field label="Packing Group">
              <b-input
                v-model="selectedDangerousGoodInfo.packing_group"
                name="packingGroup"
                placeholder="Packing Group"
                v-validate="'required'"
                disabled
              ></b-input>
            </b-field>
          </div>

          <!-- Dangerous Good Type -->
          <div class="column is-4">
            <ValidationProvider
              :rules="'required'"
              v-slot="{ errors, valid }"
              :name="'storage state'"
            >
              <b-field
                :label="'STORAGE STATE'"
                :type="{'is-danger': errors.length }"
                :message="errors[0]"
              >
                <multiselect
                  v-validate="'required'"
                  v-model="selectedDangerousGoodType"
                  deselect-label="Dangerous Good Storage State must be selected"
                  track-by="id"
                  label="label"
                  placeholder="Select Storage State"
                  :name="'storage state'"
                  :options="dangerousGoodTypes"
                  :searchable="true"
                  :allow-empty="false"
                  @select="(option,id) => handleSelectDangerousGoodType(option)"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
                </multiselect>
              </b-field>
            </ValidationProvider>
          </div>

          <!-- Gross Mass -->
          <div class="column is-4" v-if="dangerousGoodType === 'SOLID'">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.dangerous_good_packaging.gross_mass"
              :vuexKey="'dangerous_good_packaging.gross_mass'"
              :label="'Gross Mass (kg)'"
              :errorLabel="`Gross Mass`"
              :name="'grossMass'"
              :placeholder="'Gross Mass'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Net Mass -->
          <div class="column is-4" v-if="dangerousGoodType === 'SOLID'">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.dangerous_good_packaging.net_mass"
              :vuexKey="'dangerous_good_packaging.net_mass'"
              :label="'Net Mass (kg)'"
              :errorLabel="`Net Mass`"
              :name="'netMass'"
              :placeholder="'Net Mass'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Volume -->
          <div class="column is-4" v-if="dangerousGoodType=== 'FLUID'">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.dangerous_good_packaging.volume"
              :vuexKey="'dangerous_good_packaging.volume'"
              :label="'Volume (Metric Tonnes)'"
              :errorLabel="`Volume`"
              :name="'volume'"
              :placeholder="'Volume'"
              @input="updateVuex"
            ></input-with-validation>
          </div>
        </div>

        <div
          class="has-margin-bottom-40 has-margin-top-10 has-border-bottom"
          v-if="hasDangerousGoodInfo"
        >
          <h2
            class="is-size-4 has-text-weight-bold is-uppercase has-letter-spacing-3 is-black"
          >Dangerous Good Environmental Risk</h2>
        </div>

        <div class="columns is-multiline">
          <!-- Flash Point -->
          <div class="column is-4">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.flash_point"
              :vuexKey="'flash_point'"
              :label="'Flash Point'"
              :errorLabel="`Flash Point`"
              :name="'flashPoint'"
              :placeholder="'Flash Point'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Marine Pollutant -->
          <div class="column is-4">
            <ValidationProvider
              :rules="'required'"
              v-slot="{ errors, valid }"
              :name="'marine pollutant'"
            >
              <b-field
                :label="'MARINE POLLUTANT'"
                :type="{'is-danger': errors.length }"
                :message="errors[0]"
              >
                <multiselect
                  v-model="marinePollutant"
                  deselect-label="Selected"
                  track-by="id"
                  label="label"
                  placeholder="Select a Value"
                  :name="'marine pollutant'"
                  :options="booleanOptions"
                  :searchable="true"
                  :allow-empty="true"
                  @select="(option,id) => updateVuex({key: 'marine_pollutant', value: option.value})"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
                </multiselect>
              </b-field>
            </ValidationProvider>
          </div>

          <!-- Limited Quantity -->
          <div class="column is-4">
            <ValidationProvider
              :rules="'required'"
              v-slot="{ errors, valid }"
              :name="'limited quantity'"
            >
              <b-field
                :label="'LIMITED QUANTITY'"
                :type="{'is-danger': errors.length }"
                :message="errors[0]"
              >
                <multiselect
                  v-model="limitedQuantity"
                  deselect-label="Selected"
                  track-by="id"
                  label="label"
                  placeholder="Select a Value"
                  :name="'limited quantity'"
                  :options="booleanOptions"
                  :searchable="true"
                  :allow-empty="true"
                  @select="(option,id) => updateVuex({key: 'limited_quantity', value: option.value})"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
                </multiselect>
              </b-field>
            </ValidationProvider>
          </div>

          <!-- Temperature Controlled -->
          <div class="column is-4">
            <ValidationProvider
              :rules="'required'"
              v-slot="{ errors, valid }"
              :name="'temperature controlled'"
            >
              <b-field
                :label="'TEMPERATURE CONTROLLED'"
                :type="{'is-danger': errors.length }"
                :message="errors[0]"
              >
                <multiselect
                  v-model="temperatureControlled"
                  deselect-label="Selected"
                  track-by="id"
                  label="label"
                  placeholder="Select a Value"
                  :name="'temperature controlled'"
                  :options="booleanOptions"
                  :searchable="true"
                  :allow-empty="true"
                  @select="(option,id) => updateVuex({key: 'temperature_controlled', value: option.value})"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
                </multiselect>
              </b-field>
            </ValidationProvider>
          </div>

          <!-- Control Temperature -->
          <template v-if="temperatureControlled">
            <div class="column is-4" v-if="temperatureControlled.value === true">
              <input-with-validation
                :rules="'required|decimal'"
                :model="dangerousGood.control_temperature"
                :vuexKey="'control_temperature'"
                :label="'Control Temperature (°C)'"
                :errorLabel="`Control Temperature`"
                :name="'controlTemperature'"
                :placeholder="'Control Temperature'"
                @input="updateVuex"
              ></input-with-validation>
            </div>
          </template>

          <!-- Emergency Temperature -->
          <div class="column is-4">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.emergency_temperature"
              :vuexKey="'emergency_temperature'"
              :label="'Emergency Temperature (°C)'"
              :errorLabel="`Emergency Temperature`"
              :name="'emergencyTemperature'"
              :placeholder="'Emergency Temperature'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Empty/Laden Tk 
          <div class="column is-4">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.empty_laden_tank"
              :vuexKey="'empty_laden_tank'"
              :label="'Empty/Laden Tk'"
              :errorLabel="`Empty/Laden Tk`"
              :name="'emptyLadenTk'"
              :placeholder="'Empty/Laden Tk'"
              @input="updateVuex"
            ></input-with-validation>
          </div>
          -->
          
          <!-- Net Explosive Quantity -->
          <div class="column is-4">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.net_explosive_quantity"
              :vuexKey="'net_explosive_quantity'"
              :label="'Net Explosive Quantity'"
              :errorLabel="`Net Explosive Quantity`"
              :name="'netExplosiveQuantity'"
              :placeholder="'Net Explosive Quantity'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- IMDG Code Segregation Groups -->
          <div class="column is-4">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.imdg_code_segregation_group"
              :vuexKey="'imdg_code_segregation_group'"
              :label="'IMDG Code Segregation Group'"
              :errorLabel="`IMDG Code Segregation Group`"
              :name="'imdgCodeSegregationGroup'"
              :placeholder="'IMDG Code Segregation Group'"
              @input="updateVuex"
            ></input-with-validation>
          </div>
        </div>

        <div
          class="has-margin-bottom-40 has-margin-top-10 has-border-bottom"
          v-if="hasDangerousGoodInfo"
        >
          <h2
            class="is-size-4 has-text-weight-bold is-uppercase has-letter-spacing-3 is-black"
          >Dangerous Good Packaging Details</h2>
        </div>

        <div class="columns is-multiline">
          <!-- Outer Packaging -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.dangerous_good_packaging.outer_packaging"
              :vuexKey="'dangerous_good_packaging.outer_packaging'"
              :label="'Outer Packaging'"
              :errorLabel="`Outer Packaging`"
              :name="'outerPackaging'"
              :placeholder="'Outer Packaging'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Inner Packaging -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.dangerous_good_packaging.inner_packaging"
              :vuexKey="'dangerous_good_packaging.inner_packaging'"
              :label="'Inner Packaging'"
              :errorLabel="`Inner Packaging`"
              :name="'innerPackaging'"
              :placeholder="'Inner Packaging'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Tare Mass -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.tare_mass"
              :vuexKey="'tare_mass'"
              :label="'Tare Mass (Kg)'"
              :errorLabel="`Tare Mass`"
              :name="'tareMass'"
              :placeholder="'Tare Mass'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Total Gross Mass -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.total_gross_mass"
              :vuexKey="'total_gross_mass'"
              :label="'Total Gross Mass (Including Tare) (Kg)'"
              :errorLabel="`Total Gross Mass`"
              :name="'totalGrossMass'"
              :placeholder="'Total Gross Mass'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Container Identification Number -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.container_identification_number"
              :vuexKey="'container_identification_number'"
              :label="'Container Identification Number'"
              :errorLabel="`Container Identification Number`"
              :name="'containerIdentificationNumber'"
              :placeholder="'Container Identification Number'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Seal Number -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.seal_number"
              :vuexKey="'seal_number'"
              :label="'Seal Number'"
              :errorLabel="`Seal Number`"
              :name="'sealNumber'"
              :placeholder="'Seal Number'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Container Operator -->
          <div class="column is-4">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.container_operator"
              :vuexKey="'container_operator'"
              :label="'Container Operator'"
              :errorLabel="`Container Operator`"
              :name="'containerOperator'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Container Type -->
          <div class="column is-4">
            <input-with-validation
              :rules="'required'"
              :model="dangerousGood.container_type"
              :vuexKey="'container_type'"
              :label="'Container Type'"
              :errorLabel="`Container Type`"
              :name="'containerType'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Container Size -->
          <div class="column is-4">
            <input-with-validation
              :rules="'required|decimal'"
              :model="dangerousGood.container_size"
              :vuexKey="'container_size'"
              :label="'Container Size (ft)'"
              :errorLabel="`Container Size`"
              :name="'containerSize'"
              @input="updateVuex"
            ></input-with-validation>
          </div>
        </div>
      </template>
      <!-- </div> -->
    </validation-observer>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Multiselect from "vue-multiselect";
import InputWithValidation from "./form-inputs/InputWithValidation";
import MultiSelectInputWithValidation from "./form-inputs/MultiSelectInputWithValidation";

import { SHIPPER_MUTATION_TYPES } from "@/store/mutation-types";
import { createNamespacedHelpers } from "vuex";
const { mapMutations, mapGetters } = createNamespacedHelpers("shippers");

export default {
  name: "DangerousGood",

  components: {
    ValidationObserver,
    ValidationProvider,
    InputWithValidation,
    Multiselect,
    MultiSelectInputWithValidation
  },

  props: {
    dangerousGoodIndex: { type: Number, required: true },
    data: { type: Object, required: true },
    onDeleteDangerousGood: { type: Function, required: true }
  },

  data() {
    return {
      dangerousGood: this.data,
      selectedDangerousGoodInfo: null,
      selectedDangerousGoodType: null,
      marinePollutant: null,
      limitedQuantity: null,
      temperatureControlled: null,
      dangerousGoodType: null,
      booleanOptions: [
        { id: 1, value: true, label: "YES" },
        { id: 2, value: false, label: "NO" }
      ],
      dangerousGoodTypes: [
        { id: 1, value: "SOLID", label: "SOLID/GAS" },
        { id: 2, value: "FLUID", label: "LIQUID" }
      ]
    };
  },

  mounted() {
    // Set the selected input in the form to what is in the vuex store
    this.selectedDangerousGoodInfo = this.dangerousGoodsInfo.find(dgInfo => {
      return dgInfo.id === this.data.dangerous_good_info_id;
    });

    this.marinePollutant = this.booleanOptions.find(option => {
      return option.value === this.data.marine_pollutant;
    });

    this.limitedQuantity = this.booleanOptions.find(option => {
      return option.value === this.data.limited_quantity;
    });

    this.temperatureControlled = this.booleanOptions.find(option => {
      return option.value === this.data.temperature_controlled;
    });
  },

  computed: {
    ...mapGetters({
      dangerousGoodsInfo: "getDangerousGoodsInfo"
    }),

    /**
     * Determine the text to be used as the dangerous good observer ref
     */
    observerRef() {
      return `dangerousGood-${this.dangerousGoodIndex}-observer`;
    },

    // Determine if a dangerous good info has been selected for the dangerous good
    hasDangerousGoodInfo() {
      if (this.selectedDangerousGoodInfo) {
        return this.selectedDangerousGoodInfo.id !== null;
      }
      return false;
    }
  },

  methods: {
    ...mapMutations({
      commitDangerousGoodsUpdate:
        SHIPPER_MUTATION_TYPES.UPDATE_SHIPPER_DANGEROUS_GOODS_DETAILS
    }),

    /**
     * Update the vuex store when any input in the form is updated
     */
    updateVuex({ key, value }) {
      this.commitDangerousGoodsUpdate({
        index: this.dangerousGoodIndex,
        keyName: key,
        value
      });
    },

    /**
     * Update the dngerous_good_info_id
     */
    updateDangerousGoodInfoId(dangerousGoodInfo) {
      this.commitDangerousGoodsUpdate({
        index: this.dangerousGoodIndex,
        keyName: "dangerous_good_info_id",
        value: dangerousGoodInfo.id
      });
    },

    /**
     * Delete this dangerous good
     */
    deleteDangerousGood: function() {
      this.$dialog.confirm({
        title: "Deleting dangerous good",
        message:
          "Are you sure you want to <b>delete</b> this dangerous good? This action cannot be undone.",
        confirmText: "Yes, Delete",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => {
          this.onDeleteDangerousGood(this.dangerousGoodIndex);
        }
      });
    },

    handleSelectDangerousGoodType: function(option) {
      this.dangerousGoodType = option.value;
    }
  }
};
</script>
